/**
 * Constants for GraphicalView component
 * Contains menu options and configuration values
 */

import type { MenuOption } from '@saviynt/design-system';

/**
 * Category menu options for grouping by Region or Cost Center
 */
export const CATEGORY_MENU_OPTIONS: MenuOption[] = [
  { label: 'Region', value: 'region' },
  { label: 'Cost Center', value: 'cost-center' },
];

/**
 * Metric menu options for displaying Annual Cost or Utilization
 */
export const METRIC_MENU_OPTIONS: MenuOption[] = [
  { label: 'Annual Cost', value: 'annual-cost' },
  { label: 'Utilization', value: 'utilization' },
];

/**
 * Time period menu options for filtering data by time range
 */
export const TIME_PERIOD_MENU_OPTIONS: MenuOption[] = [
  { label: 'Last 30 Days', value: 'last-30-days' },
  { label: 'Last Quarter', value: 'last-quarter' },
  { label: 'Last 6 Months', value: 'last-6-months' },
  { label: 'Last Year', value: 'last-year' },
];

/**
 * Default values for GraphicalView state
 */
export const DEFAULT_GROUP_BY_OPTION = 'vendor';
export const DEFAULT_CATEGORY_OPTION = 'region';
export const DEFAULT_METRIC_OPTION = 'annual-cost';
export const DEFAULT_TIME_PERIOD_OPTION = 'last-30-days';

/**
 * Group by option constants for comparisons
 */
export const GROUP_BY_OPTIONS = {
  VENDOR: 'vendor',
  DEPARTMENT: 'department',
  CATEGORY: 'category',
} as const;

/**
 * Category option constants for comparisons
 */
export const CATEGORY_OPTIONS = {
  REGION: 'region',
  COST_CENTER: 'cost-center',
} as const;

/**
 * Chart configuration constants
 */
export const CHART_WIDTH = 910;
export const CHART_HEIGHT = 250;

/**
 * Overlay metric configuration
 */
export const OVERLAY_METRIC_CONFIG = {
  label: 'Utilization',
  formatter: (value: number) => `${value}%`,
  color: '#95979e',
  renderType: 'label' as const,
};

/**
 * Gauge configuration
 */
export const GAUGE_CONFIG = {
  value: 1700000,
  total: 2000000,
  rotation: -45,
  label: 'License Optimization Savings',
};

/**
 * Gauge legend items
 */
export const GAUGE_LEGEND_ITEMS = [
  {
    value: '-2.4%',
    logo: 'ServiceNow',
  },
  {
    value: '-3.9%',
    logo: 'Salesforce',
  },
];

/**
 * Optimization widgets configuration
 */
export const OPTIMIZATION_WIDGETS = [
  {
    text: 'Save $218k on renewals in the next 30 days',
  },
  {
    text: 'Improve your optimization score by 4%',
  },
];

/**
 * Toggle button configuration for view mode selection
 */
export const TOGGLE_BUTTONS = [
  {
    label: 'Vendor',
    value: 'vendor',
    className: 'GraphicalView-toggleBtn-start',
  },
  {
    label: 'Department',
    value: 'department',
    className: 'GraphicalView-toggleBtn-start-center',
  },
];

/**
 * Stats configuration for different view modes
 */
export const STATS_CONFIG = {
  vendor: [
    {
      value: '$34.6M',
      trend: '+3.6%',
      trendType: 'up' as const,
      label: 'Total License Spend',
    },
    {
      value: '81%',
      trend: '+2.7%',
      trendType: 'up' as const,
      label: 'Average Utilization',
    },
    {
      value: '$2,874',
      trend: '+3.2%',
      trendType: 'up' as const,
      label: 'Cost per Employee',
    },
    {
      value: '83%',
      trend: '-1.2%',
      trendType: 'down' as const,
      label: 'Optimization Score',
    },
  ],
  department: [
    {
      value: '42',
      trend: '+3.6%',
      trendType: 'up' as const,
      label: 'Total Departments',
    },
    {
      value: '3.2',
      trend: '+2.7%',
      trendType: 'up' as const,
      label: 'Average Licenses per Employee',
    },
    {
      value: 'HR',
      trend: '+3.2%',
      trendType: 'up' as const,
      label: 'Highest Spend Dept',
    },
    {
      value: 'Finance',
      trend: '-1.2%',
      trendType: 'down' as const,
      label: 'Most Underutilized',
    },
  ],
  category: [
    {
      value: '12',
      trend: '+3.6%',
      trendType: 'up' as const,
      label: 'Total Categories',
    },
    {
      value: 'Productivity',
      trend: '+2.7%',
      trendType: 'up' as const,
      label: 'Largest Category',
    },
    {
      value: 'Design',
      trend: '+3.2%',
      trendType: 'up' as const,
      label: 'Best Utilized',
    },
    {
      value: 'ERP',
      trend: '-1.2%',
      trendType: 'down' as const,
      label: 'Worst Utilized',
    },
  ],
};

/**
 * Symbol constants for GraphicalView
 */
export const GRAPHICAL_VIEW_SYMBOLS = {
  COLON: ':',
} as const;
